import { api, type PaginatedResult } from '../../lib/apiClient';
import type { DriverOrder, Incident, Notification, OrderDetail } from '../../types/api';

/**
 * Capa de acceso del panel del chofer.
 *
 * Las cuatro operaciones que llevan fotografia viajan como `multipart/form-data`
 * y no como JSON con la imagen en base64: base64 infla el cuerpo un tercio, y
 * el chofer sube fotos desde datos moviles.
 *
 * Ninguna llamada lleva el identificador del chofer. El backend lo toma de la
 * sesion y comprueba que la orden es suya; un parametro aqui solo serviria para
 * que alguien intentara cambiarlo.
 */
export interface DriverOrderListParams {
  page?: number;
  limit?: number;
  include_closed?: boolean;
}

export interface ConfirmItemPayload {
  quantity: string;
  operationKey: string;
  photo: File | Blob;
  caption?: string;
}

export interface ReportIncidentPayload {
  type: 'DAMAGE' | 'MISSING' | 'OTHER';
  affectedQuantity?: string;
  description: string;
  operationKey: string;
  photo: File | Blob;
}

/** Un equipo no lleva cantidad: es una pieza con numero de serie. */
export interface ConfirmAssetPayload {
  operationKey: string;
  photo: File | Blob;
  caption?: string;
}

export interface ReportAssetIncidentPayload {
  type: 'DAMAGE' | 'MISSING' | 'OTHER';
  description: string;
  operationKey: string;
  photo: File | Blob;
}

/**
 * Quien firma no se escribe cuando la parada trae responsable: el servidor lee
 * su nombre de la ficha del contacto. El nombre solo viaja cuando el chofer
 * declara que firmo un sustituto, o cuando la parada se armo sin responsable.
 */
export interface SignStopPayload {
  signature: Blob;
  signerName?: string;
  signerEmail?: string;
  signedBySubstitute?: boolean;
}

function itemForm(payload: ConfirmItemPayload): FormData {
  const form = new FormData();
  form.append('quantity', payload.quantity);
  form.append('operation_key', payload.operationKey);
  if (payload.caption) {
    form.append('caption', payload.caption);
  }
  form.append('photos', payload.photo, 'foto.jpg');
  return form;
}

function assetForm(payload: ConfirmAssetPayload): FormData {
  const form = new FormData();
  form.append('operation_key', payload.operationKey);
  if (payload.caption) {
    form.append('caption', payload.caption);
  }
  form.append('photos', payload.photo, 'foto.jpg');
  return form;
}

export const driverApi = {
  listMine: (params: DriverOrderListParams): Promise<PaginatedResult<DriverOrder>> =>
    api.getPaginated<DriverOrder>('/driver/orders', {
      query: {
        page: params.page,
        limit: params.limit,
        include_closed: params.include_closed ? 'true' : undefined,
      },
    }),

  getMine: (id: string): Promise<OrderDetail> => api.get<OrderDetail>(`/driver/orders/${id}`),

  arrive: (stopId: string): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/stops/${stopId}/arrive`),

  completeStop: (stopId: string): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/stops/${stopId}/complete`),

  startTransit: (orderId: string): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/orders/${orderId}/start-transit`),

  confirmPickup: (itemId: string, payload: ConfirmItemPayload): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/items/${itemId}/pickup`, itemForm(payload)),

  confirmDelivery: (itemId: string, payload: ConfirmItemPayload): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/items/${itemId}/delivery`, itemForm(payload)),

  reportIncident: (itemId: string, payload: ReportIncidentPayload): Promise<OrderDetail> => {
    const form = new FormData();
    form.append('type', payload.type);
    if (payload.affectedQuantity) {
      form.append('affected_quantity', payload.affectedQuantity);
    }
    form.append('description', payload.description);
    form.append('operation_key', payload.operationKey);
    form.append('photos', payload.photo, 'incidente.jpg');
    return api.post<OrderDetail>(`/driver/items/${itemId}/incident`, form);
  },

  confirmAssetPickup: (
    transferAssetId: string,
    payload: ConfirmAssetPayload,
  ): Promise<OrderDetail> =>
    api.post<OrderDetail>(`/driver/transfer-assets/${transferAssetId}/pickup`, assetForm(payload)),

  confirmAssetDelivery: (
    transferAssetId: string,
    payload: ConfirmAssetPayload,
  ): Promise<OrderDetail> =>
    api.post<OrderDetail>(
      `/driver/transfer-assets/${transferAssetId}/delivery`,
      assetForm(payload),
    ),

  reportAssetIncident: (
    transferAssetId: string,
    payload: ReportAssetIncidentPayload,
  ): Promise<OrderDetail> => {
    const form = new FormData();
    form.append('type', payload.type);
    form.append('description', payload.description);
    form.append('operation_key', payload.operationKey);
    form.append('photos', payload.photo, 'incidente.jpg');
    return api.post<OrderDetail>(`/driver/transfer-assets/${transferAssetId}/incident`, form);
  },

  sign: (stopId: string, payload: SignStopPayload): Promise<OrderDetail> => {
    const form = new FormData();
    if (payload.signedBySubstitute) {
      form.append('signed_by_substitute', 'true');
    }
    if (payload.signerName) {
      form.append('signer_name', payload.signerName);
    }
    if (payload.signerEmail) {
      form.append('signer_email', payload.signerEmail);
    }
    form.append('signature', payload.signature, 'firma.png');
    return api.post<OrderDetail>(`/driver/stops/${stopId}/signature`, form);
  },
};

export const incidentsApi = {
  list: (params: {
    page?: number;
    limit?: number;
    status?: 'OPEN' | 'RESOLVED' | '';
    order_id?: string;
  }): Promise<PaginatedResult<Incident>> =>
    api.getPaginated<Incident>('/incidents', {
      query: { ...params, status: params.status || undefined },
    }),

  getById: (id: string): Promise<Incident> => api.get<Incident>(`/incidents/${id}`),

  resolve: (id: string, notes: string): Promise<Incident> =>
    api.post<Incident>(`/incidents/${id}/resolve`, { resolution_notes: notes }),
};

export const notificationsApi = {
  list: (params: {
    page?: number;
    limit?: number;
    only_unread?: boolean;
  }): Promise<PaginatedResult<Notification>> =>
    api.getPaginated<Notification>('/notifications', {
      query: {
        page: params.page,
        limit: params.limit,
        only_unread: params.only_unread ? 'true' : undefined,
      },
    }),

  markRead: (ids?: string[]): Promise<{ updated: number; unread: number }> =>
    api.post<{ updated: number; unread: number }>('/notifications/read', { ids }),
};

export const driverKeys = {
  orders: ['driver', 'orders'] as const,
  order: (id: string) => ['driver', 'orders', id] as const,
  incidents: ['incidents', 'list'] as const,
  notifications: ['notifications'] as const,
};
